import {RectNode, RectNodeModel, h} from '@logicflow/core';
import {resolveExecStyle, EXEC_STATUS} from '../../../exec-status';

function hasClonedBy(properties = {}) {
  const v = properties.clonedBy;
  if (!v) {
    return false;
  }
  if (typeof v === 'string') {
    return !!v;
  }
  return !!(v.key);
}

class ApprovalNode extends RectNode {
  getShape() {
    const {model} = this.props;
    const {x, y, width, height, properties} = model;
    let approvalName = '审批人';
    if (Array.isArray(properties.users)) {
      approvalName = ' ' + properties.users.map((v) => v.name);
    }

    const left = x - width / 2;
    const top = y - height / 2;
    const isClone = hasClonedBy(properties);
    const isWeak = !!properties.weak;
    const strokeDasharray = isWeak ? '6,4' : undefined;
    const colors = resolveExecStyle(properties, {
      fill: '#E6F7FF',
      stroke: '#1890FF',
      text: '#1890FF',
      icon: '#1890FF',
    });
    const doneColors = EXEC_STATUS.done;
    const partialDone = !!properties._clonePartialDone;

    const userIcon = h('svg', {
      x: left + 5,
      y: top + 5,
      width: 16,
      height: 16,
      viewBox: '0 0 1024 1024',
      fill: colors.icon,
    }, [
      h('path', {
        d: 'M512 512a256 256 0 1 0 0-512 256 256 0 0 0 0 512z m0 85.333333c-170.666667 0-512 85.333333-512 256v85.333334h1024v-85.333334c0-170.666667-341.333333-256-512-256z',
      }),
    ]);

    const text = h('text', {
      x: left + 25,
      y: top + 18,
      fill: colors.text,
      fontSize: 12,
      textAnchor: 'start',
    }, isWeak ? `${approvalName}（弱）` : approvalName);

    const doms = [];

    if (isClone) {
      // 堆叠：最远一张在部分完成时显示完成色（示意即可，不严格匹配数量）
      const stackOffsets = [
        {dx: 10, dy: -10, fill: '#F0F5FF', stroke: '#91D5FF', opacity: 0.85},
        {dx: 5, dy: -5, fill: '#E6F7FF', stroke: '#69C0FF', opacity: 0.95},
      ];
      stackOffsets.forEach((s, i) => {
        const useDone = partialDone && i === 0;
        doms.push(h('rect', {
          x: left + s.dx,
          y: top + s.dy,
          width,
          height,
          fill: useDone ? doneColors.fill : s.fill,
          stroke: useDone ? doneColors.stroke :
            (colors.stroke === '#BFBFBF' ? s.stroke : colors.stroke),
          strokeWidth: 1.5,
          strokeDasharray,
          rx: 10,
          ry: 10,
          opacity: s.opacity,
        }));
      });
    }

    doms.push(h('rect', {
      x: left,
      y: top,
      width,
      height,
      fill: colors.fill,
      stroke: colors.stroke,
      strokeWidth: 2,
      strokeDasharray,
      rx: 10,
      ry: 10,
    }));
    doms.push(userIcon);
    doms.push(text);

    if (isClone) {
      const label = properties.clonedBy?.control?.name ||
        properties.clonedBy?.key ||
        properties.clonedKey ||
        '克隆';
      doms.push(h('text', {
        x: left + 8,
        y: top + height - 10,
        fill: '#52C41A',
        fontSize: 10,
        textAnchor: 'start',
      }, `× ${label}`));
    }

    if (isWeak && !isClone) {
      doms.push(h('text', {
        x: left + width - 8,
        y: top + height - 10,
        fill: colors.text,
        fontSize: 10,
        textAnchor: 'end',
      }, '弱审批'));
    }

    const timeoutSec = Number(properties.timeout);
    if (timeoutSec > 0) {
      const typeLabel = properties.timeoutType === '强制关闭' ? '强制' : '预警';
      let label = `${timeoutSec}秒`;
      if (timeoutSec % 86400 === 0) label = `${timeoutSec / 86400}天`;
      else if (timeoutSec % 3600 === 0) label = `${timeoutSec / 3600}时`;
      else if (timeoutSec % 60 === 0) label = `${timeoutSec / 60}分`;
      doms.push(h('text', {
        x: left + width - 8,
        y: top + 18,
        fill: properties.timeoutType === '强制关闭' ? '#CF1322' : '#8C8C8C',
        fontSize: 10,
        textAnchor: 'end',
      }, `${label}·${typeLabel}`));
    }

    return h('g', {}, doms);
  }
}

class ApprovalNodeModel extends RectNodeModel {
  initNodeData(e) {
    super.initNodeData(e);
    this.width = 160;
    this.height = 80;
    if (!this.properties || Object.keys(this.properties).length == 0) {
      this.properties = {
        'status': '审批中',
        'executor-type': '发起人自选',
        'opinionMode': '可选',
      };
    }
  }

  getProps() {
    return [{
      key: 'executor-type',
      label: '审批人类型',
      control: 'Select',
      option: {
        options: ['发起人自选', '固定'],
      },
      group: '审批',
      desc: '发起人自选：发起时指定；固定：使用默认审批人或范围',
    }, {
      key: 'approval-region',
      label: '审批人范围',
      control: 'ACLSelect',
      group: '审批',
      desc: '可选审批人的组织/角色范围限制',
    }, {
      key: 'multiple',
      label: '审批人多选',
      control: 'Check',
      group: '审批',
      desc: '开启后该节点可选择多名审批人',
    }, {
      key: 'opinionMode',
      label: '审批意见',
      control: 'Select',
      option: {
        options: ['必须', '可选', '表单'],
        defaultTo: '可选',
      },
      group: '审批',
      desc: '必须：提交前须填写意见；可选：可不填；表单：意见在表单内填写，右侧仅提交。非「表单」时可在表单权限中绑定意见与是否同意',
    }, {
      key: 'default-users',
      label: '默认审批人',
      control: 'UserSelect',
      group: '审批',
      desc: '未配置克隆时的默认审批人；有克隆对象时请用「分类默认人」',
    }, {
      key: 'clonedDefaultUsers',
      label: '分类默认人',
      control: 'CloneDefaultUsers',
      group: '审批',
      desc: '为每个克隆分类配置默认审批人',
    }, {
      key: 'message',
      label: '短信通知',
      control: 'Check',
      group: '通知',
      desc: '到达本节点时是否发送短信提醒',
    }, {
      key: 'weak',
      label: '弱审批',
      control: 'Check',
      group: '高级',
      desc: '下一节点处理人可跳过本弱审批节点直接处理',
    }, {
      key: 'timeout',
      label: '时效时长',
      control: 'Timeout',
      option: {
        placeholder: '留空表示不限时',
        defaultUnit: '小时',
      },
      group: '时效',
      desc: '本节点待办的处理时限，留空表示不限时',
    }, {
      key: 'timeoutType',
      label: '时效类型',
      control: 'Select',
      option: {
        options: ['仅预警', '强制关闭'],
        defaultTo: '仅预警',
      },
      group: '时效',
      desc: '仅预警：超时标记提醒；强制关闭：超时自动跳过并推进',
    }, {
      key: 'clonedBy',
      label: '克隆对象',
      control: 'FieldSelect',
      group: '高级',
      desc: '按表单中某个数组字段拆成多个并行审批线程，每个取值一条',
    }, {
      key: 'parallelVisible',
      label: '平行可见',
      control: 'Check',
      group: '高级',
      desc: '关闭时只看到自己的克隆线程；开启后可查看平行线程但只能填写自己的',
    }, {
      key: 'independentExecutors',
      label: '独立负责人',
      control: 'Check',
      group: '高级',
      desc: '各克隆分类使用独立负责人，不与同克隆对象的其它节点自动同步',
    }, {
      key: 'clonedType',
      label: '克隆类型',
      control: 'Select',
      option: {
        options: ['文本', '部门', '成员'],
        defaultTo: '文本',
      },
      group: '高级',
      desc: '克隆项的展示与解析类型，影响分类名称和默认人匹配',
    }];
  }
}

export default {
  type: 'approval_node',
  view: ApprovalNode,
  model: ApprovalNodeModel,
};
